import React, { useState } from "react";
import { GraduationCap, Building, Code, Wrench } from "lucide-react";
import "../timeline-styles.scss";

// Drop your real photos in ../assets/timeline/ and uncomment these, then
// pass them into timelineData below (img: img2017, etc). Left out for now
// since these are personal photos I never had — everything below renders
// fine with just text until you wire images back in.
import overview from "../assets/aboutme/brann01.png";
import img2017 from "../assets/aboutme/2017.png";
import img2018 from "../assets/aboutme/2018.png";
import img2021 from "../assets/aboutme/2021.png";
import img2024 from "../assets/aboutme/2024.png";

const timelineData = [
  {
    year: "Overall",
    isOverall: true,
  },
  {
    year: "2017",
    icon: GraduationCap,
    title: "The design journey begins",
    content:
      "My background in Interior Architecture gave me a rigorous foundation in spatial systems and human-centered planning. It taught me how to navigate complex constraints and collaborate across multidisciplinary teams — skills that later became essential when designing digital ecosystems.",
    img: img2017,
  },
  {
    year: "2018",
    icon: Building,
    title: "Shaping my design mindset with international teams",
    content:
      "While designing commercial interiors and products for a global clientele — from Italy to East and Southeast Asia — I began to notice a shift: the physical and digital worlds were converging. This realization pushed me to bridge that gap by learning full-stack development, enabling me to build the digital products I had previously only imagined.",
    img: img2018,
  },
  {
    year: "2021",
    icon: Code,
    title: "Adding code to my toolbelt",
    content:
      "While my technical training spans the full stack, my passion lives at the intersection of design and front-end development. I focus on creating high-fidelity, accessible interfaces where clean code meets intentional user research.",
    img: img2021,
  },
  {
    year: "2022",
    icon: Wrench,
    title: "Deepening my UX and front-end development practice",
    content:
      "During my graduate studies in Michigan, I modernized my design toolbelt. I moved beyond aesthetics to adopt data-driven methodologies, including heuristic evaluations, A/B testing, and usability testing, applying them directly to real-world products for startups and established organizations.",
    img: img2024,
  },
  {
    year: "2024",
    icon: Code,
    title: "UX developer in the real world",
    content:
      "Today, I work as a hybrid designer and developer. I specialize in re-architecting fragmented user flows and aligning stakeholder goals to deliver efficient, inclusive products that put the end user first.",
  },
];

// Horizontal bar: [Overall]---[2017]---[2018]---[2021]---[2022]---[2024]
// "Overall" is just another entry in the same list, so it gets the exact
// same active/highlight treatment as every year — no special-casing in
// the markup. Starts on "Overall" so there's content visible immediately,
// no click needed; clicking any pill swaps the panel below to match.
function Timeline() {
  const [active, setActive] = useState(0);
  const current = timelineData[active];

  return (
    <div className="timeline">
      <div className="timeline-bar">
        {timelineData.map((item, i) => {
          const Icon = item.icon;
          const isActive = active === i;
          return (
            <React.Fragment key={item.year}>
              {i > 0 && <div className="timeline-connector" />}
              <button
                type="button"
                className={"timeline-node" + (isActive ? " is-active" : "")}
                onClick={() => setActive(i)}
                style={{ padding: "4px 12px" }}
              >
                {Icon && (
                  <span className="timeline-node-icon">
                    <Icon size={14} />
                  </span>
                )}
                <span className="timeline-node-year">{item.year}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="timeline-details">
        {current.isOverall ? (
          <div className="timeline-bio">
            <h3 className="timeline-details-title">Hello, Brann's here!</h3>
            <p>
              I've spent the last eight years thinking deeply about how people
              interact with the world around them. My journey started with
              large-scale, "outside-in" design and eventually led me to what I
              care about most: UX development and digital products. To me,{" "}
              <b>design is an act of care</b>. If we aren't putting people at
              the center, we're not really solving problems. I believe that
              caring <b>for everyone involved</b> is often the best way to care
              for the product itself.
            </p>
            <p>
              The human side: I'm an <b>ENFJ and a Type 9w1</b>, which is a
              fancy way of saying I'm a people person who values empathy,
              collaboration, and a healthy team dynamic. I'm moving to Chicago
              soon, but my curiosity travels everywhere. I'm a big fan of maps,
              history, and painting.
            </p>
            <p>
              If you want to talk about geography or the latest book you're
              reading, we'll probably get along just fine. 🌎
            </p>
            {overview && (
              <img
                src={overview}
                alt="collage"
                style={{ width: "80%", margin: "24px 0" }}
              />
            )}
          </div>
        ) : (
          <>
            <h3 className="timeline-details-title">{current.title}</h3>
            <p className="timeline-details-body">{current.content}</p>
            {current.img && (
              <img className="timeline-details-img" src={current.img} alt="" />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Timeline;
